import React, { useEffect, useState } from 'react';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import Espaçador from '../../../Componentes/Espaçador';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Label,
  Tooltip,
} from 'recharts';
import SelectInput from '../../../Componentes/SelectInput';
import CardDashboardVazio from '../../../Componentes/CardDashboardVazio';
import DateInput from '../../../Componentes/DateInput';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import { useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { apiService } from '../../../Services/ApiService';
import DashboardTabs from './DashboardTabs';

export default function DashBoardAmbiente() {
  const [isPrinting, setIsPrinting] = useState(false);
  const [dashboard, setDashboard] = useState([]);
  const componentRef = useRef();
  const isMobile = useMediaQuery({ query: `(max-width: 920px)` });
  const { get } = apiService();
  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });
  const [hasSelectedEnvironment, setHasSelectedEnvironment] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEnvironmentLabel, setSelectedEnvironmentLabel] = useState('Selecione');
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState(null);
  const [selectedPeriodValue, setSelectedPeriodValue] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false);
  const [hasSelectedDate, setHasSelectedDate] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [initialPeriodDone, setInitialPeriodDone] = useState(false);
  const [selectedTimeInterval, setSelectedTimeInterval] = useState(null);

  const timeIntervals = [
    { value: 'every_5_minutes', label: '5 minutos' },
    { value: 'every_15_minutes', label: '15 minutos' },
    { value: 'every_30_minutes', label: '30 minutos' },
    { value: 'every_hour', label: '1 hora' },
  ];

  const periods = [
    { value: 'daily', label: 'Diário' },
    { value: 'last_7_days', label: 'Últimos 7 dias' },
    { value: 'last_15_days', label: 'Últimos 15 dias' },
    { value: 'last_30_days', label: 'Últimos 30 dias' },
  ];

  useEffect(() => {
    if (selectedEnvironment && selectedDate && selectedPeriodValue) {
      getDashboard();
    }
  }, [selectedEnvironment, selectedDate, selectedPeriodValue, selectedTimeInterval]);

  const handleSelecionarPeriod = (selected) => {
    setSelectedPeriodLabel(selected.label);
    setSelectedPeriodValue(selected.value);
    setHasSelectedPeriod(true);
    if (selected.value !== 'daily') {
      setSelectedTimeInterval(null);
    }
  };

  const handleTimeIntervalSelect = (selected) => {
    setSelectedTimeInterval(selected.value);
  };

  const handleEnvironmentSelect = (env) => {
    setSelectedEnvironmentLabel(env.label);
    setSelectedEnvironment(env);
    setHasSelectedEnvironment(true);
  };

  const getDashboard = async () => {
    try {
      if (selectedEnvironment && selectedDate && selectedPeriodValue) {
        setIsLoading(true);
        const { value: environmentId } = selectedEnvironment;
        const formattedDate = selectedDate.toISOString();
        const period = selectedPeriodValue;
        const timeInterval = selectedTimeInterval
          ? `&alternateTimeInterval=${selectedTimeInterval}`
          : '';
        const endpoint = `dashboard/environment?environmentId=${environmentId}&date=${formattedDate}&timePeriod=${period}${timeInterval}`;
        const response = await get(endpoint);

        if (response && response.temperatureData && response.humidityData) {
          const {
            temperatureData,
            humidityData,
            averageTemperature,
            averageHumidity,
            minTemperature,
            maxTemperature,
            minHumidity,
            maxHumidity,
            maxTemperatureOffset,
            minTemperatureOffset,
            minHumidityOffset,
            maxHumidityOffset,
          } = response;

          if (temperatureData.length > 0 && humidityData.length > 0) {
            setDashboard([
              {
                temperatureData,
                humidityData,
                averageTemperature,
                averageHumidity,
                minTemperature,
                maxTemperature,
                minHumidity,
                maxHumidity,
                maxTemperatureOffset,
                minTemperatureOffset,
                minHumidityOffset,
                maxHumidityOffset,
              },
            ]);
          } else {
            setDashboard([]);
          }
        } else {
          setModalError({ visible: true, message: 'Resposta inválida da API' });
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setModalError({ visible: true, message: 'Erro ao buscar dados do ambiente' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEnvironments();
  }, []);

  useEffect(() => {
    if (environments.length > 0) {
      const firstEnvironment = environments[0];
      handleEnvironmentSelect(firstEnvironment);
    }
  }, [environments]);

  useEffect(() => {
    if (periods.length > 0 && !initialPeriodDone) {
      const firstPeriod = periods[0];
      handleSelecionarPeriod(firstPeriod);
      setInitialPeriodDone(true);
    }
  }, [periods, initialPeriodDone]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setSelectedDate(today);
  }, []);

  const getEnvironments = async () => {
    setIsLoading(true);
    try {
      const data = await get('environments/all');

      if (data) {
        const environmentsList = data || [];
        setEnvironments(environmentsList.map((env) => ({ value: env.id, label: env.name })));
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar ambientes' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CorpoPagina loading={isLoading}>
      {modalError.visible && (
        <ModalConfirmacao
          aviso={modalError.message}
          erro={true}
          clicado={() => setModalError({ visible: false, message: '' })}
        />
      )}
      <div className="ContainerGeral" ref={componentRef}>
        {!isPrinting ? <DashboardTabs /> : null}
        <TopicoTexto titulo="Dashboard: Ambiente" largura={isMobile ? '100%' : '80%'} />
        {!isPrinting ? (
          <h3 style={{ color: '#005288' }}>Procedimento de Referência:13-FS20-1000-00002-AM</h3>
        ) : null}
        <div className="CamposDashboard" style={{ width: isMobile ? '100%' : '98%' }}>
          <div className="CamposDashboard Esquerda" style={{ width: isMobile ? '100%' : '75%' }}>
            <SelectInput
              top={0}
              bottom={12}
              padrao={{ value: '', label: selectedEnvironmentLabel }}
              titulo="Ambiente"
              largura={isMobile ? '90%' : '30%'}
              itens={environments}
              clicado={handleEnvironmentSelect}
            />
            {selectedPeriodValue === 'daily' && (
              <>
                <Espaçador espaço={16} />
                <SelectInput
                  top={0}
                  titulo="Intervalo de Tempo"
                  bottom={12}
                  padrao={{
                    value: '',
                    label: selectedTimeInterval
                      ? timeIntervals.find((interval) => interval.value === selectedTimeInterval)
                          .label
                      : 'Selecione',
                  }}
                  largura={isMobile ? '90%' : '30%'}
                  itens={timeIntervals}
                  clicado={handleTimeIntervalSelect}
                />
              </>
            )}
            <Espaçador espaço={16} />
            <DateInput
              top={0}
              titulo="Data Selecionada"
              largura={isMobile ? '90%' : '30%'}
              clicado={(d) => {
                setSelectedDate(d ? new Date(d) : null);
                setHasSelectedDate(true);
              }}
              padrao={selectedDate}
              erro={!hasSelectedDate}
              erroTexto="Selecione a data"
            />
            <Espaçador espaço={16} />
            <SelectInput
              top={0}
              titulo="Selecione o Período"
              bottom={12}
              padrao={{ value: '', label: selectedPeriodLabel }}
              largura={isMobile ? '90%' : '30%'}
              itens={periods}
              clicado={handleSelecionarPeriod}
            />
          </div>
          <div className="CamposDashboard Direita" style={{ width: isMobile ? '18%' : '0%' }}>
            <img
              className="Direita"
              src={require('../../../Icones/icone_impressão.svg').default}
              alt="Impressão"
              onClick={() => {
                setIsPrinting(true);
                setTimeout(() => {
                  window.print();
                  setIsPrinting(false);
                }, 500);
              }}
            />
          </div>
        </div>
        <div className="DashboardContainer">
          <div className="DashboardFlexContainer">
            <div className="DashTemp">
              <CardDashboardVazio
                top={!isPrinting ? 32 : -32}
                width={!isPrinting ? '100%' : 960}
                height={!isPrinting ? 600 : 600}
                maxW={3000}
              >
                <div className="TemperatureGraphHeader">
                  <h4 id="FonteCard" style={{ paddingLeft: 16 }}>
                    GRÁFICO DE TEMPERATURA
                  </h4>
                </div>
                <div className="GraphContainer">
                  <div className="statView">
                    <div className="ConfiguredValuesContainer">
                      <p className="ConfiguredValuesText" style={{ fontWeight: 'bold' }}>
                        Padrão
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '30px' }}>
                        <p>Min: {dashboard.map((items) => items.minTemperature)} °C</p>
                        <p>Máx: {dashboard.map((items) => items.maxTemperature)} °C</p>
                      </div>
                    </div>
                    <div className="DailyValueContainer">
                      <p className="DailyValueTitle" style={{ fontWeight: 'bold' }}>
                        Resultado diário
                      </p>
                      <p style={{ marginLeft: '50px' }}>
                        {dashboard.map((items) => items.averageTemperature)} °C
                      </p>
                    </div>
                    {/* <div style={{ marginLeft: 'auto' }}>
                    <SelectInput
                      top={-35}
                      bottom={0}
                      padrao={{ value: '', label: selectedPeriodLabel }}
                      // largura={'20%'}
                      itens={periods}
                      clicado={handleSelecionarPeriod}
                    />
                  </div> */}
                  </div>

                  <ResponsiveContainer
                    width={!isPrinting ? '100%' : 960}
                    height={!isPrinting ? '65%' : 300}
                  >
                    <LineChart width={500} height={100} data={dashboard[0]?.temperatureData}>
                      <CartesianGrid stroke="#ccc" strokeWidth={2} strokeDasharray="3 3" fill="" />
                      <XAxis dataKey="time" angle={0} />
                      <YAxis
                        yAxisId="left"
                        domain={[
                          0,
                          dashboard.length > 0
                            ? Math.max(...dashboard.map((item) => item.maxTemperature))
                            : null,
                        ]}
                        label={
                          <Label
                            dx={-22}
                            angle={270}
                            style={{ fontFamily: 'montserrat', fontSize: 15 }}
                          >
                            Temperatura (°C)
                          </Label>
                        }
                        position="top"
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        label={
                          <Label
                            dx={10}
                            angle={270}
                            style={{ fontFamily: 'montserrat', fontSize: '15' }}
                          >
                            Temperatura (°C)
                          </Label>
                        }
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="temperature"
                        stroke="#205284"
                        activeDot={{ r: 8 }}
                        dot={true}
                        isAnimationActive={false}
                      />
                      <Tooltip
                        content={({ payload, label }) => {
                          if (payload && payload.length > 0) {
                            const {
                              payload: { temperature },
                            } = payload[0];
                            return (
                              <div style={{ backgroundColor: 'white', padding: '5px' }}>
                                <p>Hora: {label}</p>
                                <p>Temperatura: {temperature} °C</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      //* green area =
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.minTemperatureOffset}
                        y2={dashboard[0]?.maxTemperatureOffset}
                        fill="#6FB433"
                      />
                      //? yellow area superior
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.maxTemperatureOffset}
                        y2={dashboard[0]?.maxTemperature}
                        fill="#E67E03"
                      />
                      //? yellow area inferior
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.minTemperature}
                        y2={dashboard[0]?.minTemperatureOffset}
                        fill="#E67E03"
                      />
                      //! Red area
                      <ReferenceArea
                        yAxisId="left"
                        y1={
                          dashboard[0]?.temperatureData && dashboard[0]?.temperatureData.length > 0
                            ? Math.min(
                                ...dashboard[0].temperatureData.map((item) => item.temperature),
                              ) > 0
                              ? 0
                              : Math.min(
                                  ...dashboard[0].temperatureData.map((item) => item.temperature),
                                )
                            : undefined
                        }
                        y2={dashboard[0]?.minTemperature}
                        fill="#E8061D"
                      />
                      {dashboard.length > 0 && (
                        <ReferenceArea
                          yAxisId="left"
                          y1={dashboard[0]?.maxTemperature}
                          // y2={Math.max(
                          //   ...dashboard[0].temperatureData.map((item) => item.temperature),
                          // )}
                          fill="#E8061D"
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="legendContainer">
                  <div className="legendItem">
                    <div
                      className="legendCircleReading"
                      style={{ backgroundColor: 'white', borderColor: '#2E5280' }}
                    />
                    <p className="legend">LEITURA</p>
                  </div>
                  <div className="legendItemTime">
                    <div className="legendCircle" />
                    <p style={{ fontFamily: 'montserrat', fontSize: 15, color: '' }}>-Tempo-</p>
                  </div>
                  <div className="legendItemColor">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#6FB433' }} />
                      <p className="legend">DENTRO DO PADRÃO</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#E67E03' }} />
                      <p className="legend">ÁREA DE ATENÇÃO</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#E8061D' }} />
                      <p className="legend">FORA DO PADRÃO</p>
                    </div>
                  </div>
                </div>
              </CardDashboardVazio>
            </div>
            <div className="DashHumid">
              <CardDashboardVazio
                height={600}
                top={32}
                width={!isPrinting ? '100%' : 960}
                maxW={3000}
              >
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <h4 id="FonteCard" style={{ paddingLeft: 16 }}>
                    GRÁFICO DE UMIDADE
                  </h4>
                </div>
                <div className="GraphContainer">
                  <div className="statView">
                    <div className="ConfiguredValuesContainer">
                      <p className="ConfiguredValuesText" style={{ fontWeight: 'bold' }}>
                        Padrão
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '30px' }}>
                        <p>Min: {dashboard.map((items) => items.minHumidity)} %</p>
                        <p>Máx: {dashboard.map((items) => items.maxHumidity)} %</p>
                      </div>
                    </div>
                    <div className="DailyValueContainer">
                      <p className="DailyValueTitle" style={{ fontWeight: 'bold' }}>
                        Resultado diário
                      </p>
                      <p style={{ marginLeft: '50px' }}>
                        {dashboard.map((items) => items.averageHumidity)} %
                      </p>
                    </div>
                  </div>
                  <ResponsiveContainer
                    width={!isPrinting ? '100%' : 960}
                    height={!isPrinting ? '65%' : 300}
                  >
                    <LineChart width={500} height={100} data={dashboard[0]?.humidityData}>
                      <CartesianGrid stroke="#ccc" strokeWidth={2} strokeDasharray="3 3" />
                      <XAxis dataKey="time" angle={0} />
                      <YAxis
                        yAxisId="left"
                        domain={[0, dashboard.length > 0 ? 100 : null]}
                        label={
                          <Label
                            dx={-10}
                            angle={270}
                            style={{ fontFamily: 'montserrat', fontSize: 15 }}
                          >
                            Umidade (%)
                          </Label>
                        }
                        position="top"
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        label={
                          <Label
                            dx={-10}
                            angle={270}
                            style={{ fontFamily: 'montserrat', fontSize: 15 }}
                          >
                            Umidade (%)
                          </Label>
                        }
                        position="top"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="humidity"
                        stroke="#205284"
                        activeDot={{ r: 8 }}
                        dot={true}
                        isAnimationActive={false}
                      />
                      <Tooltip
                        content={({ payload, label }) => {
                          if (payload && payload.length > 0) {
                            const {
                              payload: { humidity },
                            } = payload[0];
                            return (
                              <div style={{ backgroundColor: 'white', padding: '5px' }}>
                                <p>Hora: {label}</p>
                                <p>Umidade: {humidity} %</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      //* green area =
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.minHumidityOffset}
                        y2={dashboard[0]?.maxHumidityOffset}
                        fill="#6FB433"
                      />
                      //? yellow area superior
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.maxHumidityOffset}
                        y2={dashboard[0]?.maxHumidity}
                        fill="#E67E03"
                      />
                      //? yellow area inferior
                      <ReferenceArea
                        yAxisId="left"
                        y1={dashboard[0]?.minHumidity}
                        y2={dashboard[0]?.minHumidityOffset}
                        fill="#E67E03"
                      />
                      //! Red area
                      <ReferenceArea
                        yAxisId="left"
                        y1={0}
                        y2={dashboard[0]?.minHumidity}
                        fill="#E8061D"
                      />
                      {dashboard.length > 0 && (
                        <ReferenceArea
                          yAxisId="left"
                          y1={dashboard[0]?.maxHumidity}
                          // y2={100}
                          fill="#E8061D"
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="legendContainer">
                  <div className="legendItem">
                    <div
                      className="legendCircleReading"
                      style={{ backgroundColor: 'white', borderColor: '#2E5280' }}
                    />
                    <p className="legend">LEITURA</p>
                  </div>
                  <div className="legendItemTime">
                    <div className="legendCircle" />
                    <p style={{ fontFamily: 'montserrat', fontSize: 15, color: '' }}>-Tempo-</p>
                  </div>
                  <div className="legendItemColor">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#6FB433' }} />
                      <p className="legend">DENTRO DO PADRÃO</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#E67E03' }} />
                      <p className="legend">ÁREA DE ATENÇÃO</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="legendCircle" style={{ backgroundColor: '#E8061D' }} />
                      <p className="legend">FORA DO PADRÃO</p>
                    </div>
                  </div>
                </div>
              </CardDashboardVazio>
            </div>
          </div>
        </div>
      </div>
    </CorpoPagina>
  );
}
